import {
  CheckCircleFilled,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CreditCardOutlined,
  EnvironmentOutlined,
  SettingOutlined,
  TruckOutlined,
} from '@ant-design/icons';
import { CardActions } from '@portal/actions/index';
import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { getRouteStackPath } from '@portal/config/routes';
import { useNavigation } from '@portal/hooks/useNavigation';
import useReduxState from '@portal/hooks/useReduxState';
import {
  CardMilestone,
  CardMilestoneSource,
} from '@portal/models/enumerators/cards';
import { translate } from '@portal/services/i18n';
import { maskCEP } from '@portal/services/masks';
import { formatDateWithHours } from '@portal/utils/format';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import './Tracking.less';

const MILESTONE_ICONS: Record<CardMilestone, React.ReactNode> = {
  [CardMilestone.CARD_REQUESTED]: <CreditCardOutlined />,
  [CardMilestone.SENT_TO_EMBOSSING]: <SettingOutlined />,
  [CardMilestone.FORWARDED_TO_CARRIER]: <TruckOutlined />,
  [CardMilestone.CARD_DELIVERED]: <CheckCircleFilled />,
};

const Tracking = () => {
  const dispatch = useDispatch();
  const navigate = useNavigation();
  const params: { id: string } = useParams();
  const {
    cards: { trackingInfo },
    user: { me: loggedUser },
  } = useReduxState();

  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    dispatch(
      CardActions.trackCard(params?.id ?? '', (err) => {
        if (err) {
          setHasError(true);
        }
      })
    );
  }, [dispatch, params?.id]);

  const getSourceLabel = (source: CardMilestoneSource) =>
    translate(`PAGES.CARDS.MY_CARDS.TRACKING_CARD.SOURCE.${source}`);

  const getMilestoneLabel = (milestone: CardMilestone) =>
    translate(`PAGES.CARDS.MY_CARDS.TRACKING_CARD.MILESTONE.${milestone}`);

  const emptyOrErrorState = () => {
    const title = hasError
      ? translate('PAGES.CARDS.MY_CARDS.TRACKING_CARD.ERROR_STATE.TITLE')
      : translate('PAGES.CARDS.MY_CARDS.TRACKING_CARD.EMPTY_STATE.TITLE');

    const description = hasError
      ? translate('PAGES.CARDS.MY_CARDS.TRACKING_CARD.ERROR_STATE.DESCRIPTION')
      : translate('PAGES.CARDS.MY_CARDS.TRACKING_CARD.EMPTY_STATE.DESCRIPTION');

    const icon = hasError ? <CloseCircleOutlined /> : <TruckOutlined />;

    return (
      <div className="tracking-card__empty">
        <div className="tracking-card__empty__icon">{icon}</div>
        <ThemedText className="tracking-card__empty__title">{title}</ThemedText>
        <ThemedText>{description}</ThemedText>
      </div>
    );
  };

  const renderMilestones = () => {
    if (!trackingInfo?.milestones?.length) return null;

    const visibleMilestones = [...trackingInfo.milestones]
      .filter((item) => item.source !== CardMilestoneSource.CARRIER)
      .sort((a, b) => a.step - b.step);

    return (
      <div className="tracking-card__steps">
        <ThemedText className="tracking-card__steps__title">
          {translate('PAGES.CARDS.MY_CARDS.TRACKING_CARD.STEPS_TITLE')}
        </ThemedText>
        <div className="tracking-card__steps__list">
          {visibleMilestones.map((item, index) => {
            const { completed, occurredAt, milestone } = item;
            const statusMap = {
              isCompleted: completed,
              isPending: !completed,
            };
            const isLast = index === visibleMilestones.length - 1;
            const isStepTwo = item.step === 2;
            const formattedDate = occurredAt
              ? isStepTwo
                ? DateTime.fromISO(occurredAt, { setZone: true }).toFormat(
                    'dd/MM/yyyy HH:mm'
                  )
                : formatDateWithHours(occurredAt)
              : '';
            const milestoneKey = milestone as CardMilestone;

            return (
              <div
                key={item.step}
                className={`tracking-card__step ${
                  statusMap.isCompleted ? 'tracking-card__step--completed' : ''
                } ${statusMap.isPending ? 'tracking-card__step--pending' : ''}`}
              >
                <div className="tracking-card__step__indicator">
                  <div className="tracking-card__step__icon">
                    {MILESTONE_ICONS[milestoneKey] || <ClockCircleOutlined />}
                  </div>
                  {!isLast && <div className="tracking-card__step__line" />}
                </div>
                <div className="tracking-card__step__content">
                  <div className="tracking-card__step__header">
                    <ThemedText className="tracking-card__step__title">
                      {getMilestoneLabel(milestoneKey) || item.description}
                    </ThemedText>
                    <span
                      className={`tracking-card__step__status-chip ${
                        statusMap.isCompleted
                          ? 'tracking-card__step__status-chip--done'
                          : 'tracking-card__step__status-chip--waiting'
                      }`}
                    >
                      {statusMap.isCompleted
                        ? translate(
                            'PAGES.CARDS.MY_CARDS.TRACKING_CARD.STATUS.COMPLETED'
                          )
                        : translate(
                            'PAGES.CARDS.MY_CARDS.TRACKING_CARD.STATUS.PENDING'
                          )}
                    </span>
                  </div>
                  <ThemedText className="tracking-card__step__description">
                    {item.description}
                  </ThemedText>
                  <div className="tracking-card__step__meta">
                    {formattedDate ? (
                      <span className="tracking-card__step__date">
                        {formattedDate}
                      </span>
                    ) : null}
                    <span className="tracking-card__step__source">
                      {statusMap.isCompleted ? (
                        <CheckCircleFilled />
                      ) : (
                        <ClockCircleOutlined />
                      )}
                      {getSourceLabel(item.source as CardMilestoneSource)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAccountAddress = () => {
    if (!loggedUser?.account?.address) return null;

    return (
      <div className="tracking-card__status-banner">
        <div className="tracking-card__status-banner__icon">
          <EnvironmentOutlined />
        </div>
        <div className="tracking-card__status-banner__content">
          <ThemedText className="tracking-card__status-banner__label">
            {translate('PAGES.CARDS.MY_CARDS.TRACKING_CARD.ADDRESS_DELIVERY')}
          </ThemedText>
          <ThemedText className="tracking-card__account-address__content">
            {`${loggedUser.account?.address?.street}, ${
              loggedUser.account?.address?.addressNumber
            }${
              loggedUser.account?.address?.complement
                ? `, ${loggedUser.account?.address?.complement}`
                : ''
            } - ${loggedUser.account?.address?.neighborhood}, ${
              loggedUser.account?.address?.city
            } - ${loggedUser.account?.address?.state}, ${maskCEP(
              loggedUser.account?.address?.zipCode ?? ''
            )}`}
          </ThemedText>
        </div>
      </div>
    );
  };

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.HOME'),
            url: getRouteStackPath('HOME', 'HOME_PAGE'),
          },
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.CARDS'),
            url: getRouteStackPath('CARDS', 'MY_CARDS_PAGE'),
          },
          {
            active: false,
            title: translate('PAGES.CARDS.MY_CARDS.TRACKING_CARD.TITLE'),
          },
        ]}
      />
      <div className="tracking-card">
        <AdvancedPanelContentHeader
          testId="tracking-card-header-panel"
          title={translate('PAGES.CARDS.MY_CARDS.TRACKING_CARD.TITLE')}
          description={translate(
            'PAGES.CARDS.MY_CARDS.TRACKING_CARD.DESCRIPTION'
          )}
          goBackAction={() =>
            navigate(getRouteStackPath('CARDS', 'MY_CARDS_PAGE'))
          }
        />
        <AdvancedCard testId="tracking-card-content-panel">
          {trackingInfo ? (
            <>
              {renderAccountAddress()}
              {renderMilestones()}
            </>
          ) : (
            emptyOrErrorState()
          )}
        </AdvancedCard>
      </div>
    </>
  );
};

export default Tracking;
